'use client'
import { useQuery } from '@apollo/client/react'
import * as Ariakit from '@ariakit/react'
import { useId, useMemo } from 'react'
import styled from 'styled-components'

import {
  ALERT_INTENT,
  Alert,
  ArrowOutlink,
  SelectOption,
  Skeleton,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../__generated__/graphql/catalog'
import { OrganizationMinimalFieldsFragment } from '../../../__generated__/graphql/catalog/graphql'
import { useGraphqlClients } from '../../_providers/GraphqlClientsProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import * as FormPrimitives from '../form/FormPrimitives.client'
import { FormSelect } from '../form/FormSelect'

import * as InformationalDialog from './InformationalDialog/InformationalDialog.client'

const OrganizationSelectionFormSection = styled.div`
  margin-top: ${o => o.theme.spacing(5)};
`

const OrganizationSelectionFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(5)};
`

const StyledArrowOutlink = styled(ArrowOutlink)`
  margin-left: ${o => o.theme.spacing(2)};
`

const ORGANIZATION_SELECTION_QUERY = gql(`
  query OrganizationSelectionQuery {
    my {
      organizations {
        ...OrganizationMinimalFields
      }
    }
  }
`)

/** Skeleton representation of the organization selection form. */
function OrganizationSelectionFormSkeleton() {
  return (
    <OrganizationSelectionFormContainer>
      <FormPrimitives.FormControlWrapper>
        <Skeleton width='100px' height='16.5px' animated />
        <Skeleton width='100%' height='42px' animated />
      </FormPrimitives.FormControlWrapper>
    </OrganizationSelectionFormContainer>
  )
}

type OrganizationSelectionFormProps = {
  /** The user's associated organizations that they have admin/owner permissions for. */
  organizations: OrganizationMinimalFieldsFragment[]
  /** The ID to apply for the underlying form element. */
  formId: string
}

/**
 * The form that is displayed within the `OrganizationSelectionDialog`.
 *
 * On submission of the form within this dialog, the user is navigated to the organization dashboard
 * for the organization that they selected in a new tab.
 */
export function OrganizationSelectionForm({
  organizations,
  formId,
}: OrganizationSelectionFormProps) {
  const {
    magnolia: {
      organization: { general: generalOrganizationStrings },
    },
  } = useTranslation()

  const form = Ariakit.useFormStore<{ organizationId: string }>({
    defaultValues: {
      organizationId: organizations[0]?.id ?? '',
    },
  })

  const dialog = Ariakit.useDialogContext()

  form.useSubmit(state => {
    if (organizations.find(o => o.id === state.values.organizationId)) {
      dialog?.setOpen(false)
      window.open(`/organizations/${state.values.organizationId}`, '_blank')
    }
  })

  return (
    <Ariakit.Form id={formId} store={form} resetOnSubmit={false}>
      <OrganizationSelectionFormContainer>
        <FormPrimitives.FormControlWrapper>
          <Ariakit.FormLabel render={<FormPrimitives.FormLabel />} name='organizationId'>
            {generalOrganizationStrings.organization}
          </Ariakit.FormLabel>
          <FormSelect
            name='organizationId'
            onChange={val => form.setValue('organizationId', val)}
            renderSelectValue={val => organizations.find(org => org.id === val)?.name}
          >
            {organizations
              .filter(org => Boolean(org.name))
              .map(org => (
                <SelectOption key={org.id} value={org.id}>
                  {org.name}
                </SelectOption>
              ))}
          </FormSelect>
        </FormPrimitives.FormControlWrapper>
      </OrganizationSelectionFormContainer>
    </Ariakit.Form>
  )
}

/**
 * The inner content of the Organization Selection dialog.
 *
 * Responsible for fetching the current user's associated organizations
 * for use within the form's dropdown.
 */
export function OrganizationSelectionDialogContent() {
  const {
    magnolia: {
      general: generalStrings,
      organization: {
        dialog: { organizationSelection: organizationSelectionDialogStrings },
      },
    },
  } = useTranslation()

  // Define the form ID that should be applied to the inner form + associated with the submission button
  const formId = useId()

  const { personalizedClient } = useGraphqlClients()

  // Fetch the user's available organizations
  const { data, loading, error } = useQuery(ORGANIZATION_SELECTION_QUERY, {
    client: personalizedClient,
  })

  const availableAdminOrganizations = useMemo(
    () =>
      (data?.my.organizations ?? []).filter(org =>
        ['OWNER', 'ADMIN'].includes(org.currentUserRole)
      ),
    [data?.my.organizations]
  )

  return (
    <>
      <InformationalDialog.Header closeText={generalStrings.close}>
        <InformationalDialog.Title>
          {organizationSelectionDialogStrings.title}
        </InformationalDialog.Title>
      </InformationalDialog.Header>
      <InformationalDialog.Body>
        <InformationalDialog.Description>
          {organizationSelectionDialogStrings.description}
        </InformationalDialog.Description>
        <OrganizationSelectionFormSection>
          {/* Display loading state while available organizations are being fetched */}
          {loading && <OrganizationSelectionFormSkeleton />}
          {/* Display alert message when critical request fails */}
          {error && (
            <Alert intent={ALERT_INTENT.Error}>
              <Alert.Message>{organizationSelectionDialogStrings.loadingError}</Alert.Message>
            </Alert>
          )}
          {/* If successful, display the form component or alert if no organizations */}
          {!loading && !error && data && (
            <>
              {availableAdminOrganizations.length > 0 ? (
                <OrganizationSelectionForm
                  organizations={availableAdminOrganizations}
                  formId={formId}
                />
              ) : (
                <Alert intent={ALERT_INTENT.Info}>
                  <Alert.Message>
                    {organizationSelectionDialogStrings.noAvailableOrganizations}
                  </Alert.Message>
                </Alert>
              )}
            </>
          )}
        </OrganizationSelectionFormSection>
      </InformationalDialog.Body>
      <InformationalDialog.Controls>
        <InformationalDialog.Button
          type='submit'
          form={formId}
          disabled={loading || error || !data || availableAdminOrganizations.length === 0}
        >
          {organizationSelectionDialogStrings.submitCta}
          <StyledArrowOutlink />
        </InformationalDialog.Button>
      </InformationalDialog.Controls>
    </>
  )
}

/**
 * A dialog that displays a list of the organizations that the user has permission to access
 * as a team owner/admin. Allows the the user to navigate to the organization dashboard for a
 * selected organization.
 *
 * This dialog is responsible for fetching the user's associated organizations.
 */
export function OrganizationSelectionDialog() {
  return (
    <InformationalDialog.Dialog analyticsTitle='Associated Organizations' variant='default'>
      <OrganizationSelectionDialogContent />
    </InformationalDialog.Dialog>
  )
}
